import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtMoney } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Trophy, TrendingUp, Sparkles, RefreshCcw, BookOpen } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from "recharts";
import LeagueTrophies from "./LeagueTrophies";

const COLORS = ["#EAB308", "#22C55E", "#3B82F6", "#F43F5E", "#A855F7", "#F97316", "#14B8A6", "#EC4899", "#84CC16", "#06B6D4"];

export default function LeagueStorico() {
  const { league } = useLeague();
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [compareTeam, setCompareTeam] = useState(""); // 2° squadra opzionale
  const [aiStory, setAiStory] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  async function loadAiStory({ force = false } = {}) {
    setAiLoading(true);
    setAiOpen(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/history/ai-story`, { params: force ? { force: true } : {} });
      setAiStory(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setAiOpen(false);
    } finally { setAiLoading(false); }
  }

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/leagues/${league.league_id}/history`); setSeasons(r.data); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [league.league_id]);

  // Costruisco i dati per il grafico Patrimonio Storico
  // { season: 1, "Milan": 500, "Juve": 480, ... }
  const { chartData, teamKeys, teamColors } = useMemo(() => {
    const seasonsAsc = [...seasons].sort((a, b) => a.season - b.season);
    const teamNames = new Set();
    for (const s of seasonsAsc) for (const t of (s.teams || [])) teamNames.add(t.team_name);
    const keys = Array.from(teamNames);
    const colorMap = Object.fromEntries(keys.map((k, i) => [k, COLORS[i % COLORS.length]]));
    const data = seasonsAsc.map(s => {
      const row = { season: `S${s.season}` };
      for (const t of (s.teams || [])) {
        // fallback: se patrimonio_end non esiste (stagioni vecchie) usa budget_end o net_delta
        const val = t.patrimonio_end ?? ((t.budget_end || 0) + (t.roster_value_end || 0)) ?? t.net_delta ?? 0;
        row[t.team_name] = Number(val) || 0;
      }
      return row;
    });
    return { chartData: data, teamKeys: keys, teamColors: colorMap };
  }, [seasons]);

  // Auto-select first team once teams are loaded
  useEffect(() => {
    if (teamKeys.length > 0 && !selectedTeam) setSelectedTeam(teamKeys[0]);
  }, [teamKeys, selectedTeam]);

  // Dati confronto storico per la squadra selezionata: Budget vs Valore Rosa per stagione
  // Se compareTeam è impostato, aggiunge le serie della 2° squadra come "B: Budget" e "B: Valore Rosa"
  const teamCompareData = useMemo(() => {
    if (!selectedTeam) return [];
    const seasonsAsc = [...seasons].sort((a, b) => a.season - b.season);
    return seasonsAsc.map(s => {
      const t1 = (s.teams || []).find(x => x.team_name === selectedTeam);
      const row = {
        season: `S${s.season}`,
        Budget: t1 ? Math.round(Number(t1.budget_end || 0)) : 0,
        "Valore Rosa": t1 ? Math.round(Number(t1.roster_value_end || 0)) : 0,
        Patrimonio: t1 ? Math.round(Number(t1.patrimonio_end || 0)) : 0,
      };
      if (compareTeam && compareTeam !== selectedTeam) {
        const t2 = (s.teams || []).find(x => x.team_name === compareTeam);
        row[`Budget · ${compareTeam}`] = t2 ? Math.round(Number(t2.budget_end || 0)) : 0;
        row[`Valore Rosa · ${compareTeam}`] = t2 ? Math.round(Number(t2.roster_value_end || 0)) : 0;
      }
      return row;
    });
  }, [seasons, selectedTeam, compareTeam]);

  const hasTeamCompareData = teamCompareData.some(r => r.Budget > 0 || r["Valore Rosa"] > 0);
  const hasCompare = compareTeam && compareTeam !== selectedTeam;

  const hasPatrimonio = chartData.some(row => teamKeys.some(k => row[k] != null && row[k] !== 0));

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">🏆 Albo d&apos;Oro & Storico</div>
          <h2 className="text-3xl font-black tracking-tight">Palmarès e archivio stagioni</h2>
          <p className="text-slate-400 text-sm mt-1">Trofei e stagioni chiuse: ogni stagione è archiviata qui in modo permanente.</p>
        </div>
        <button
          onClick={() => loadAiStory()}
          data-testid="ai-story-open-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-fuchsia-500/20 hover:from-purple-500/30 hover:to-fuchsia-500/30 border border-purple-500/40 text-purple-200 text-sm font-black uppercase tracking-widest transition-all"
        >
          <BookOpen size={16}/> Racconto AI della Lega
        </button>
      </div>

      {/* Iter 71.6 — Albo d'Oro (trofei totali) in cima */}
      <LeagueTrophies />

      <div className="pt-4 border-t border-slate-800">
        <h3 className="text-lg font-bold text-amber-300 flex items-center gap-2">
          🗄️ Stagioni Archiviate — Dettaglio anno per anno
        </h3>
      </div>

      {loading ? <div className="text-slate-500 py-10 text-center">Caricamento…</div> :
      seasons.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-lg p-10 text-center text-slate-500" data-testid="history-empty">
          <div className="text-4xl mb-2">📚</div>
          Nessuna stagione archiviata. Chiudi la stagione dal <strong className="text-amber-400">Pannello Admin</strong> per iniziare a costruire lo storico.
        </div>
      ) : (
        <>
          {/* Grafico andamento patrimonio storico */}
          {chartData.length > 0 && hasPatrimonio && (
            <div className="border border-slate-800 rounded-lg bg-slate-900 p-6" data-testid="chart-patrimonio-storico">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-emerald-400"/>
                  <h3 className="font-bold uppercase text-sm tracking-wider">📈 Andamento patrimonio</h3>
                </div>
                <span className="text-xs text-slate-500">{teamKeys.length} squadre · {chartData.length} stagioni</span>
              </div>
              <div className="h-80">
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="season" tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}/>
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v)=>`${v}`}/>
                    <Tooltip
                      contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
                      formatter={(v) => [fmtMoney(v) + ' cr', '']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
                    {teamKeys.map(k => (
                      <Line key={k} type="monotone" dataKey={k} stroke={teamColors[k]} strokeWidth={2}
                        dot={{ r: 4, fill: teamColors[k] }} activeDot={{ r: 6 }}/>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-slate-500 mt-2">💡 Patrimonio = Budget di fine stagione + Valore rosa (Qt.A × prezzo/Qt.I).</div>
            </div>
          )}

          {/* Confronto Storico Squadra: Budget vs Valore Rosa */}
          {teamKeys.length > 0 && chartData.length > 0 && (
            <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 shadow-2xl shadow-black/40" data-testid="chart-team-history">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-amber-400"/>
                  <h3 className="font-bold uppercase text-sm tracking-wider">📊 Confronto storico squadra — Budget vs Valore rosa</h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"/>
                    <select
                      data-testid="team-history-select"
                      value={selectedTeam}
                      onChange={(e)=>setSelectedTeam(e.target.value)}
                      className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-amber-500"
                    >
                      {teamKeys.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <span className="text-xs text-slate-500 font-black">vs</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-fuchsia-400 inline-block"/>
                    <select
                      data-testid="team-history-compare-select"
                      value={compareTeam}
                      onChange={(e)=>setCompareTeam(e.target.value)}
                      className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-fuchsia-500"
                    >
                      <option value="">— Nessuna (confronto singolo) —</option>
                      {teamKeys.filter(k => k !== selectedTeam).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  {hasCompare && (
                    <button
                      data-testid="team-history-compare-clear"
                      onClick={()=>setCompareTeam("")}
                      title="Rimuovi confronto"
                      className="p-2 rounded-lg border border-slate-700 text-slate-400 hover:text-rose-400 hover:border-rose-500/50 transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              {hasTeamCompareData ? (
                <div className="h-80" data-testid="team-history-chart-container">
                  <ResponsiveContainer>
                    <AreaChart data={teamCompareData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <defs>
                        <linearGradient id="grBudgetHist" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.05}/>
                        </linearGradient>
                        <linearGradient id="grRosaHist" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#34D399" stopOpacity={0.8}/>
                          <stop offset="100%" stopColor="#34D399" stopOpacity={0.05}/>
                        </linearGradient>
                        <linearGradient id="grBudgetHist2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#E879F9" stopOpacity={0.55}/>
                          <stop offset="100%" stopColor="#E879F9" stopOpacity={0.02}/>
                        </linearGradient>
                        <linearGradient id="grRosaHist2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.55}/>
                          <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                      <XAxis dataKey="season" tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'monospace' }}/>
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }}/>
                      <Tooltip
                        contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 12, fontSize: 12 }}
                        formatter={(v) => [fmtMoney(v) + ' cr', '']}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle"/>
                      <Area type="monotone" dataKey="Budget" name={`💰 Budget · ${selectedTeam}`} stroke="#F59E0B" strokeWidth={2} fill="url(#grBudgetHist)" isAnimationActive animationDuration={900}/>
                      <Area type="monotone" dataKey="Valore Rosa" name={`📊 Rosa · ${selectedTeam}`} stroke="#34D399" strokeWidth={2} fill="url(#grRosaHist)" isAnimationActive animationDuration={900}/>
                      {hasCompare && (
                        <>
                          <Area type="monotone" dataKey={`Budget · ${compareTeam}`} name={`💰 Budget · ${compareTeam}`} stroke="#E879F9" strokeWidth={2} strokeDasharray="6 3" fill="url(#grBudgetHist2)" isAnimationActive animationDuration={900}/>
                          <Area type="monotone" dataKey={`Valore Rosa · ${compareTeam}`} name={`📊 Rosa · ${compareTeam}`} stroke="#38BDF8" strokeWidth={2} strokeDasharray="6 3" fill="url(#grRosaHist2)" isAnimationActive animationDuration={900}/>
                        </>
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center text-slate-500 text-sm py-10 border border-dashed border-slate-800 rounded-2xl">
                  Nessun dato disponibile per <strong className="text-slate-300">{selectedTeam}</strong> in queste stagioni.
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-2">
                💡 Il grafico confronta 💰 <strong className="text-amber-300">Budget</strong> (cassa liquida) e 📊 <strong className="text-emerald-300">Valore rosa</strong> (Qt.A × prezzo) al termine di ogni stagione.
                {hasCompare && <> Le linee tratteggiate 🟣🔵 sono la squadra di confronto <strong className="text-fuchsia-300">{compareTeam}</strong>.</>}
              </div>
            </div>
          )}

          {/* Season cards */}
          <div className="space-y-4">
            {seasons.map(s => (
              <div key={s.season} className="border border-slate-800 rounded-lg bg-slate-900 overflow-hidden" data-testid={`season-${s.season}`}>
                <div className="bg-gradient-to-r from-amber-500/20 to-transparent border-b border-slate-800 p-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-400/20 flex items-center justify-center text-amber-400 font-black">
                      <Trophy size={20}/>
                    </div>
                    <div>
                      <div className="text-2xl font-black tracking-tight">🏆 Stagione {s.season}</div>
                      <div className="text-xs text-slate-400">Chiusa il {new Date(s.closed_at).toLocaleDateString("it-IT")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="text-xs text-slate-400">{s.teams?.length || 0} squadre · {s.loans_returned || 0} prestiti conclusi</div>
                    <SeasonRecapButton leagueId={league.league_id} season={s.season}/>
                  </div>
                </div>
                {s.notes && <div className="p-4 text-sm text-slate-400 border-b border-slate-800">📝 {s.notes}</div>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-950/60">
                      <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                        <th className="px-3 py-3">Pos.</th>
                        <th className="px-3 py-3">Squadra</th>
                        <th className="px-3 py-3 text-right">🏆 Premio</th>
                        <th className="px-3 py-3 text-right">📺 TV</th>
                        <th className="px-3 py-3 text-right">🛍️ Merch</th>
                        <th className="px-3 py-3 text-right">🏛️ Stadio</th>
                        <th className="px-3 py-3 text-right">💵 Stip.</th>
                        <th className="px-3 py-3 text-right">⭐ Ranking</th>
                        <th className="px-3 py-3 text-right">💰 Delta</th>
                        <th className="px-3 py-3 text-right">🏦 Patrimonio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(s.teams || []).sort((a,b) => a.position - b.position).map(t => {
                        const medal = t.position === 1 ? "🥇" : t.position === 2 ? "🥈" : t.position === 3 ? "🥉" : `${t.position}°`;
                        return (
                          <tr key={t.team_id} className="border-t border-slate-800">
                            <td className="px-3 py-2.5 font-black">{medal}</td>
                            <td className="px-3 py-2.5 font-bold">{t.team_name}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-amber-300">{fmtMoney(t.prize)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(t.tv_rights)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(t.merch)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(t.stadium)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-rose-300">{fmtMoney(t.salaries)}</td>
                            <td className="px-3 py-2.5 text-right font-mono text-amber-300">+{fmtMoney(t.ranking_delta)}</td>
                            <td className={`px-3 py-2.5 text-right font-mono font-bold ${t.net_delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                              {t.net_delta >= 0 ? "↗" : "↘"} {fmtMoney(t.net_delta)}
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono font-black text-emerald-300">
                              {t.patrimonio_end != null ? fmtMoney(t.patrimonio_end) : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAiOpen(false)} data-testid="ai-story-modal">
          <div onClick={e => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col rounded-3xl bg-gradient-to-br from-purple-900/40 via-slate-900 to-slate-950 border border-purple-500/40 shadow-2xl shadow-purple-500/20">
            <div className="p-5 border-b border-purple-500/30 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] text-purple-400 uppercase font-bold">📖 Racconto Storico AI</div>
                <h3 className="text-2xl font-black text-white mt-0.5 truncate">La Storia di {league.name}</h3>
                {aiStory?.generated_at && (
                  <div className="text-[10px] text-slate-500 font-mono mt-1">
                    generato il {new Date(aiStory.generated_at).toLocaleString("it-IT")} · {aiStory.n_seasons} stagioni · {aiStory.model}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {aiStory && (
                  <button onClick={() => loadAiStory({ force: true })} disabled={aiLoading}
                    data-testid="ai-story-regen-btn" title="Rigenera racconto"
                    className="text-purple-400 hover:text-purple-200 p-1.5 rounded-md border border-purple-500/30 hover:border-purple-400 disabled:opacity-50">
                    <RefreshCcw size={14}/>
                  </button>
                )}
                <button onClick={() => setAiOpen(false)} className="text-slate-500 hover:text-white p-1" data-testid="ai-story-close">
                  <span className="text-lg">×</span>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 md:p-8">
              {aiLoading && !aiStory && (
                <div className="text-center text-purple-300 py-10 text-sm animate-pulse" data-testid="ai-story-loading">
                  ✨ Sto scrivendo la storia della tua lega… potrebbero volerci 10-20 secondi
                </div>
              )}
              {aiStory && (
                <div className="prose prose-invert prose-sm max-w-none" data-testid="ai-story-text">
                  {aiStory.narrative.split(/\n\n+/).map((para, i) => (
                    <p key={i} className="text-slate-200 text-[15px] leading-[1.75] mb-4 first-letter:text-2xl first-letter:font-black first-letter:text-purple-300"
                       dangerouslySetInnerHTML={{ __html: para.replace(/^# (.+)$/gm, '<h1 class="text-3xl font-black text-white mb-4 text-center">$1</h1>').replace(/\*\*([^*]+)\*\*/g, '<strong class="text-purple-200 font-bold">$1</strong>') }}/>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function SeasonRecapButton({ leagueId, season }) {
  const [open, setOpen] = useState(false);
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leagues/${leagueId}/finances/season-recap/${season}`);
      if (r.data?.recap) {
        setRecap(r.data.recap);
        setGeneratedAt(r.data.created_at);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const generate = async (refresh = false) => {
    setLoading(true);
    try {
      const url = `/leagues/${leagueId}/finances/season-recap/${season}${refresh ? "?refresh=true" : ""}`;
      const r = await api.post(url);
      setRecap(r.data.recap);
      setGeneratedAt(r.data.created_at);
      toast.success(refresh ? "🔄 Racconto rigenerato" : "✨ Racconto AI generato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    if (!open && recap === null) await load();
    setOpen(!open);
  };

  return (
    <>
      <button
        onClick={toggle}
        data-testid={`recap-toggle-s${season}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-2xl border border-indigo-500/40 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 font-black text-xs uppercase tracking-wider shadow-md shadow-indigo-500/10 transition-all"
      >
        <Sparkles size={12}/> {open ? "Nascondi racconto" : "🎙️ Racconto AI"}
      </button>
      {open && (
        <div className="w-full mt-2 basis-full order-last rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-900/30 via-slate-950 to-slate-950 p-5 shadow-2xl" data-testid={`recap-panel-s${season}`}>
          {loading ? (
            <div className="text-slate-400 text-sm py-6 text-center">
              <div className="animate-pulse">✨ L&apos;AI Coach sta scrivendo il racconto…</div>
            </div>
          ) : recap ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-black flex items-center gap-1">
                  <Sparkles size={11}/> Racconto AI · Claude Sonnet 5
                </div>
                <button
                  onClick={()=>generate(true)}
                  data-testid={`recap-refresh-s${season}`}
                  title="Rigenera"
                  className="text-slate-400 hover:text-indigo-300"
                >
                  <RefreshCcw size={14}/>
                </button>
              </div>
              <MarkdownRecap text={recap}/>
              {generatedAt && (
                <div className="text-[9px] text-slate-600 mt-3 uppercase tracking-widest">
                  Generato: {new Date(generatedAt).toLocaleString("it-IT")}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <div className="text-slate-400 text-sm mb-3">Nessun racconto ancora generato per questa stagione.</div>
              <button
                onClick={()=>generate(false)}
                data-testid={`recap-generate-s${season}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-black text-xs uppercase tracking-wider"
              >
                <Sparkles size={14}/> Genera con AI Coach
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Micro-markdown renderer: **bold**, ## heading, # heading, bullets, paragrafi
function MarkdownRecap({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let key = 0;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { out.push(<div key={key++} className="h-2"/>); continue; }
    if (line.startsWith("# ")) {
      out.push(<h3 key={key++} className="text-xl font-black text-indigo-200 mt-4 mb-1">{line.slice(2)}</h3>);
    } else if (line.startsWith("## ")) {
      out.push(<h4 key={key++} className="text-base font-black text-amber-300 mt-3 mb-1">{line.slice(3)}</h4>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      out.push(<li key={key++} className="text-sm text-slate-300 ml-4 list-disc" dangerouslySetInnerHTML={{ __html: mdInline(line.slice(2)) }}/>);
    } else {
      out.push(<p key={key++} className="text-sm text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: mdInline(line) }}/>);
    }
  }
  return <div className="prose prose-invert prose-sm max-w-none">{out}</div>;
}

function mdInline(s) {
  // Escape base, poi bold, italic, code inline
  const esc = s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-black">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="text-amber-200">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="text-emerald-300 bg-slate-950 px-1 rounded text-xs">$1</code>');
}
